/**
 * WARNING: DO NOT ADD QUERIES TO THIS FILE UNLESS THEY ARE BONFIRE-SPECIFIC
 * In general, most queries should go in common-app
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { sortBy } from "lodash";
import uuid from "uuid";

import { gql } from "@examspark/api";
import { formatClassroomActivityKey } from "@examspark/common";
import {
  useAppContext,
  useAuthorizationContext,
  useMutationCreateClassroomActivity,
} from "@examspark/common-app";
import { IDSK, Objective } from "@examspark/types";

export const useQuerySearchBonfireObjectives = (
  scopes: string[] | undefined,
) => {
  const sortedScopes = sortBy(scopes) ?? [];
  return useQuery<Objective[]>({
    enabled: sortedScopes.length > 0,
    queryKey: ["objectives", "search", ...sortedScopes],
    queryFn: () =>
      gql.operations.objective.searchBonfireObjectives(scopes ?? []),
  });
};

// Bonfire exams are not assessable, so there's no reason to
// clone-on-assign them, or handle google classroom. But unlike our publisher apps,
// they have a unitId and need a classroom-activity record created
// See useMutationCreateClassroomActivity for the method this code is based on
export const useMutationAddExamToClassroomBonfire = () => {
  const [context] = useAppContext();
  const authorizationContext = useAuthorizationContext();
  const createClassroomActivityMutation = useMutationCreateClassroomActivity();

  return useMutation({
    mutationFn: async (params: {
      exam: IDSK;
      classroom: IDSK;
      unit: IDSK;
      name: string;
    }) => {
      const { classroom, exam, unit, name } = params;

      const examConfigId = uuid();
      const workspaceId = context.workspace?.id;
      const userId = context.user?.id;
      const appKeyId = context.appLicense?.appKey;
      const appId = context.appLicense?.appId;

      const unitId = unit.id;

      // we're still not quite good enough at setting this on new
      // workspaces. Pretend it's fine for now
      let bankId = context.appLicenseConfig?.userContentBankId;
      if (!bankId) {
        console.error(
          `Missing userContentBankId in ${workspaceId}. Using default.`,
        );
        bankId = `${workspaceId}/user-content`;
      }

      if (!workspaceId || !userId || !appKeyId || !appId || !bankId) {
        console.error({ workspaceId, userId, appKeyId, appId, bankId });
        throw new Error("Missing required fields");
      }

      const examConfigTags = [
        {
          key: "classroom-id",
          value: classroom.id,
          type: "string",
        },
        {
          key: "cohort-classroom",
          value: classroom.id,
          type: "string",
        },
        {
          key: "unit-id",
          value: params.unit.id,
        },
      ];

      const createExamConfigResult =
        await gql.operations.exam.createExamFromExamTemplate(
          exam,
          {
            id: examConfigId,
            workspaceId,
            creatorId: userId,
            appId,
            appKey: appKeyId,
            bankId,
            tags: examConfigTags,
            name: name,
          },
          {
            useOriginalExam: true,
          },
        );
      if (!createExamConfigResult) {
        console.error("No examConfig found");
        return;
      }

      // create `classroom-activity` record
      const createClassroomActivityResult =
        await createClassroomActivityMutation.mutateAsync({
          ...formatClassroomActivityKey(classroom.id, examConfigId, unitId),
          schemaVersion: "2020-01-01",
          workspaceId: workspaceId,
          creatorId: authorizationContext.userId,
          resourceId: [examConfigId, "exam-config"].join("/"),
        });

      if (!createClassroomActivityResult) {
        console.error("No createClassroomActivityResult found");
        return;
      }

      return {
        examConfig: createExamConfigResult,
        classroomActivity: createClassroomActivityResult,
      };
    },
  });
};
